import API from './api';
import { CaseFile, SENSITIVITY_FLAGS } from '../types/caseForm';

// One upload path for Create and Edit, so both send the full metadata the form collected
// (category, description with sensitivity prefixes, date, author, language, confidentiality).
// Uploads run one at a time; a failure is recorded and the rest continue.

export interface UploadOutcome {
  uploaded: number;
  failed: { name: string; message: string }[];
}

export function buildDocumentFormData(caseFile: CaseFile): FormData {
  const fd = new FormData();
  fd.append('document', caseFile.file); // route is multer .single('document')
  fd.append('category', caseFile.category);
  const prefixes = (caseFile.sensitivityWarnings || [])
    .map((w) => SENSITIVITY_FLAGS.find((f) => f.value === w)?.prefix)
    .filter(Boolean)
    .join(' ');
  const base = caseFile.description || caseFile.file.name;
  fd.append('description', prefixes ? `${prefixes} ${base}` : base);
  if (caseFile.sensitivityWarnings?.length) fd.append('sensitivityWarnings', JSON.stringify(caseFile.sensitivityWarnings));
  if (caseFile.documentDate) fd.append('documentDate', caseFile.documentDate);
  if (caseFile.authorSource) fd.append('authorSource', caseFile.authorSource);
  if (caseFile.language) fd.append('language', caseFile.language);
  if (caseFile.confidentiality) fd.append('confidentiality', caseFile.confidentiality);
  return fd;
}

export async function uploadCaseDocuments(caseId: string, files: CaseFile[]): Promise<UploadOutcome> {
  const out: UploadOutcome = { uploaded: 0, failed: [] };
  for (const caseFile of files) {
    try {
      await API.post(`/api/documents/case/${caseId}`, buildDocumentFormData(caseFile), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      out.uploaded += 1;
    } catch (err: any) {
      out.failed.push({ name: caseFile.file.name, message: err?.response?.data?.message || err?.message || 'Upload failed' });
    }
  }
  return out;
}

// "Case saved, but 2 documents did not upload: a.pdf, b.jpg." — or null when nothing failed.
export function describeUploadFailures(outcome: UploadOutcome): string | null {
  if (outcome.failed.length === 0) return null;
  const names = outcome.failed.map((f) => f.name).join(', ');
  return `Case saved, but ${outcome.failed.length} document${outcome.failed.length === 1 ? ' did' : 's did'} not upload: ${names}. Add ${outcome.failed.length === 1 ? 'it' : 'them'} again from the case page.`;
}
